import {
    Chip,
    Stack,
    Typography,
    Card,
    CardContent,
    IconButton,
    Box,
    Popper,
    Paper,
    ClickAwayListener,
    debounce,
    Skeleton,
    TextField,
    Autocomplete,
    InputAdornment,
    TooltipProps,
    tooltipClasses,
    Link,
  } from '@mui/material';
  import {
    DataGridPro,
    GridColDef,
    GridColumnGroupingModel,
    GridColumnHeaders,
    GridPaginationModel,
    GridRenderCellParams,
    GridColumnVisibilityModel,
    GridFilterModel,
    GridLogicOperator,
    GridToolbar,
    useGridApiContext,
    useGridRootProps,
    GridSortModel,
  } from '@mui/x-data-grid-pro';
  import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
  import { useCurrentWorkspace } from 'src/context/reducers/app-settings/selectors';
  import Iconify from 'src/components/iconify';
  import { FilterPanelCustom } from 'src/components/data-grid/filter-panel-custom';
  import { styled } from '@mui/material/styles';
  import { useLocales } from 'src/locales';
  import { useNonBrandedContext } from '../context/use-non-branded';
  import { useExportMutation } from 'src/context/api/exports/api';
  import { enqueueSnackbar } from 'notistack';
  import { LoadingButton } from '@mui/lab';
  import { useBuildColumns } from './hook';
import { useCompetitorsQuery } from 'src/context/api/non-branded/api';
import { fDate } from 'src/utils/format-time';
import { useSelector } from 'react-redux';
import { RootState } from 'src/context/store';
  
  
  const GridPanelRoot = styled(Popper, {
    name: 'MuiDataGrid',
    slot: 'Panel',
    overridesResolver: (props, styles) => styles.panel,
  })(({ theme }) => ({
    zIndex: (theme as any).zIndex.modal,
  }));
  const GridPaperRoot = styled(Paper, {
    name: 'MuiDataGrid',
    slot: 'Paper',
    overridesResolver: (props, styles) => styles.paper,
  })(({ theme }) => ({
    backgroundColor: '#FFFFFF',
    minWidth: 300,
    maxHeight: 450,
    display: 'flex',
    maxWidth: `calc(100vw - ${theme.spacing(0.5)})`,
    overflow: 'auto',
  }));
  
  type FilterOperator =
    | 'lt'
    | 'lte'
    | 'gt'
    | 'gte'
    | 'equal'
    | 'notEqual'
    | 'between'
    | 'contains'
    | 'doesNotContain'
    | 'startsWith'
    | 'endsWith'
    | 'isEmpty'
    | 'isNotEmpty'
    | 'isAnyOf';
  
  
  const COMPARISON_SUFFIXES = ['_compare', '_diff'] as const;
  
  const getBaseField = (field: string) =>
    COMPARISON_SUFFIXES.reduce((baseField, suffix) => baseField.replace(suffix, ''), field);
  
  const isComparisonField = (field: string) =>
    COMPARISON_SUFFIXES.some((suffix) => field.endsWith(suffix));
  
  const normalizeColumnVisibilityModel = (model: GridColumnVisibilityModel) => {
    const normalized = { ...model };
  
    Object.keys(normalized).forEach((field) => {
      if (!isComparisonField(field)) {
        return;
      }
  
      const baseField = getBaseField(field);
      if (normalized[baseField] === false) {
        normalized[field] = false;
      }
    });
  
    return normalized;
  };
  
  export default function SearchTermsGrid() {
    const { t } = useLocales();
    const workspace = useCurrentWorkspace();
    const [exportMutation] = useExportMutation();
    const { selectedRegion } = useNonBrandedContext();
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
      pageSize: 5,
      page: 0,
    });
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
    const [sortModel, setSortModel] = useState<GridSortModel>([
      { field: 'search_term', sort: 'desc' },
    ]);
    const filterStore = useSelector((state: RootState) => state.filters);
    const { data: competitorsGrid, isLoading, isError, isFetching } = useCompetitorsQuery(
      {
        query: "grid",
        devices: filterStore.devices,
        countries: selectedRegion ? [selectedRegion] : [],
        platforms: filterStore.platforms,
        workspace_id: workspace?.id as number,
        start_date: fDate(filterStore.start, 'yyyy-MM-dd'),
        end_date: fDate(filterStore.end, 'yyyy-MM-dd'),
        limit: paginationModel.pageSize,
        page: paginationModel.page,
        filter_model: filterModel.items,
      }
    );
    const opportunityMap: Record<string, string> = {
      no_opportunity: t('dashboard.nonBrand.opportunities.noOpportunity'),
      overlap: t('dashboard.nonBrand.opportunities.overlap'),
      seo_boost: t('dashboard.nonBrand.opportunities.organicBoost'),
      sea_boost: t('dashboard.nonBrand.opportunities.cpcBoost'),
    };
  
    const apiRef = useRef<any>(null);
    const toolbarRef = useRef<HTMLDivElement>(null);
    const columns = useBuildColumns('competitors');
  
    const check = (value: any) => {
      if (value === undefined) {
        return false;
      }
      if (value === null) {
        return false;
      }
      if (typeof value === 'number') {
        return value !== 0;
      }
      if (typeof value === 'string') {
        return value !== '';
      }
      if (Array.isArray(value)) {
        return value.some(check);
      }
      if (typeof value === 'object') {
        return check(Object.values(value));
      }
      return false;
    }
  
    const handlePaginationModelChange = (model: GridPaginationModel) => {
      setPaginationModel(model);
    };
  
  
    const Toolbar = (props: any) => {
      return (
        <Stack
          ref={toolbarRef}
          direction="row"
          alignItems="center"
          spacing={0}
          px={2.5}
          py={1.25}
          sx={{
            borderBottom: '1px solid rgba(145, 158, 171, 0.12)',
            minHeight: 46,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: '#333C47',
              textAlign: 'center',
              fontFamily: 'Public Sans',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: '120%',
            }}
          >
            {t('dashboard.nonBrand.competitors')}
          </Typography>
          <Box flexGrow={1} />
          <Stack direction="row" alignItems="center" px={1} spacing={0.5}>
            <IconButton
              onClick={() => apiRef.current?.showPreferences('columns')}
              sx={{
                width: 26,
                height: 26,
                padding: 0,
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <Iconify icon="mingcute:calendar-week-line" width={18} height={18} />
            </IconButton>
            <IconButton
              onClick={() => apiRef.current?.showFilterPanel()}
              sx={{
                width: 26,
                height: 26,
                padding: 0,
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <Iconify icon="ic:round-filter-list" width={18} height={18} />
            </IconButton>
            <LoadingButton
              loading={isLoading || isFetching}
              startIcon={<Iconify icon="lets-icons:export" width={16} height={16} />}
              onClick={() => {
                if (competitorsGrid?.job_id) {
                  exportMutation({
                    name: `[${workspace?.name}] (Non Branded) Competitors export`,
                    queryId: competitorsGrid?.job_id,
                    columns: columns
                      .map((column) => column.field)
                      .filter((column) => {
                        return (
                          true
                        );
                      }),
                    column_tr: columns.reduce(
                      (acc: Record<string, string>, column: GridColDef) => {
                        acc[column.field] = column.headerName as string;
                        return acc;
                      },
                      {} as Record<string, string>
                    ),
                    dimensions: ['domain_name'],
                  })
                    .unwrap()
                    .then((res) => {
                      enqueueSnackbar(
                        "Export is running in background, you will be notified when it's done",
                        { variant: 'success' }
                      );
                    })
                    .catch((err) => {
                      enqueueSnackbar('Error exporting data', { variant: 'error' });
                    });
                } else {
                  enqueueSnackbar('Unable to export this query', { variant: 'error' });
                }
              }}
              sx={{
                minWidth: 24,
                minHeight: 24,
                maxWidth: 24,
                maxHeight: 24,
                padding: 0,
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
            </LoadingButton>
          </Stack>
        </Stack>
      );
    };
  
    return (
      <Card
        sx={{
          borderRadius: 2,
          border: '1px solid rgba(145, 158, 171, 0.12)',
          overflow: 'hidden',
        }}
      >
        <CardContent
          sx={{
            px: 0,
            py: 0,
            overflow: 'visible',
            minHeight: isLoading || isFetching ? 600 : 'auto',
          }}
        >
          <DataGridPro
            classes={{
              root: 'search-term-grid',
              columnHeaders: 'search-term-grid-column-headers',
              columnHeader: 'search-term-grid-column-header',
            }}
            apiRef={apiRef}
            pinnedColumns={{ left: ['search_term'], right: ['opportunity'] }}
            loading={isLoading || isFetching}
            rows={isLoading || isFetching ? [] : competitorsGrid?.data || []}
            columns={columns}
            getRowHeight={() => 60}
            disableColumnFilter={true}
            disableColumnMenu={true}
            filterModel={filterModel}
            filterMode="server"
            sortingMode="server"
            disableColumnSelector={true}
            disableMultipleColumnsSorting={true}
            onSortModelChange={setSortModel}
            sortModel={sortModel}
            onFilterModelChange={setFilterModel}
            disableDensitySelector
            checkboxSelection={false}
            pagination
            autoHeight
            disableRowSelectionOnClick={true}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            rowCount={competitorsGrid?.total_rows || 0}
            pageSizeOptions={[10, 20, 50, 100]}
            sx={{
              '& .MuiDataGrid-toolbarContainer': {
                padding: 0,
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid rgba(145, 158, 171, 0.12)',
                minHeight: 53,
              },
            }}
            slots={{
              panel(props) {
                const apiRefContext = useGridApiContext();
                const [isPlaced, setIsPlaced] = useState(false);
  
                const handleClickAway = useCallback(() => {
                  apiRefContext.current.hidePreferences();
                }, [apiRefContext]);
  
                const handleKeyDown = useCallback(
                  (event: React.KeyboardEvent) => {
                    if (event.key === 'Escape') {
                      apiRefContext.current.hidePreferences();
                    }
                  },
                  [apiRefContext]
                );
  
                const modifiers = useMemo(
                  () =>
                    [
                      {
                        name: 'flip',
                        enabled: true,
                        options: {
                          rootBoundary: 'document',
                        },
                      },
                      {
                        name: 'isPlaced',
                        enabled: true,
                        phase: 'main' as const,
                        fn: () => {
                          setIsPlaced(true);
                        },
                        effect: () => () => {
                          setIsPlaced(false);
                        },
                      },
                    ] as any,
                  []
                );
  
                const open = apiRef.current?.state?.preferencePanel?.open;
                const panelType = apiRef.current?.state?.preferencePanel?.openedPanelValue;
                const anchorEl = toolbarRef.current;
  
                if (!anchorEl) {
                  return null;
                }
  
                const panelProps = {
                  ...props,
                  as: undefined,
                  anchorEl: anchorEl,
                  open: open,
                };
  
                return (
                  <GridPanelRoot
                    {...panelProps}
                    placement="bottom-end"
                    className={props.className}
                    modifiers={modifiers}
                  >
                    <ClickAwayListener
                      mouseEvent="onPointerUp"
                      touchEvent={false}
                      onClickAway={handleClickAway}
                    >
                      <GridPaperRoot elevation={8} onKeyDown={handleKeyDown}>
                        {isPlaced && props.children}
                      </GridPaperRoot>
                    </ClickAwayListener>
                  </GridPanelRoot>
                );
              },
              toolbar: Toolbar,
              filterPanel: (props) => (
                <FilterPanelCustom
                  {...props}
                  getColumnForNewFilter={(args) => (args ? args.columns[0].field : undefined)}
                />
              ),
              loadingOverlay(props, deprecatedLegacyContext) {
                return (
                  <Skeleton variant="rectangular" height={60 * (paginationModel.pageSize + 2)} />
                );
              },
            }}
            slotProps={{
              toolbar: {
                printOptions: {
                  disableToolbarButton: true,
                },
                csvOptions: {
                  disableToolbarButton: true,
                },
              },
              filterPanel: {
                logicOperators: [GridLogicOperator.And, GridLogicOperator.Or],
              },
              loadingOverlay: {
                style: {
                  backgroundColor: 'unset',
                },
              },
            }}
          />
        </CardContent>
      </Card>
    );
  }